import { useState, type ReactElement } from 'react';

import { CardFrame, QueryBody } from '@/components/ui/card-frame';
import { cn } from '@/components/ui/cn';
import { useToast } from '@/components/ui/toast';
import type { TrainingStage } from '@/features/document-editor/training-stage';
import { useTrainingStage } from '@/features/document-editor/use-training-stage';
import { apiErrorReason } from '@/lib/api';

import {
  rescheduleErrorText,
  rescheduleToast,
  trainingStageSummary,
} from '../training-stage-presentation';
import { useReschedule } from '../use-reschedule';
import { CloseTrainingAction } from './CloseTrainingAction';
import { RescheduleModal } from './RescheduleModal';

interface Props {
  entryId: string;
  code: string;
  companyId: string | null;
}

/** Etapa de capacitación en la ficha (G4, cierre F1): estado, fecha límite (la vigencia)
 *  y avance de lectura del LMS. Calidad puede reprogramar la vigencia con motivo y, si el
 *  LMS no reporta el documento, cerrar la etapa con motivo (G2-C). */
export function TrainingStageCard({ entryId, code, companyId }: Props): ReactElement {
  const query = useTrainingStage(entryId, { companyId, enabled: true });
  const reschedule = useReschedule();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = (): void => {
    setOpen(false);
    setError(null);
  };
  const confirm = (effectiveDate: string, reason: string): void => {
    setError(null);
    reschedule.mutate(
      { entryId, effectiveDate, reason },
      {
        onSuccess: (r) => {
          close();
          showToast(rescheduleToast(code, r.effectiveDate, r.lmsSynced));
        },
        onError: (err) => setError(rescheduleErrorText(apiErrorReason(err))),
      },
    );
  };

  return (
    <CardFrame title="Etapa de capacitación">
      <QueryBody query={query} rows={3}>
        {(t: TrainingStage) => {
          // Zona de negocio: la misma hora que el aviso del backend, sea cual sea el navegador.
          const s = trainingStageSummary(t);
          return (
            <div className="cb">
              <span className={cn('badge', s.cls)}>{s.label}</span>
              {s.lines.map((line) => (
                <p key={line} className="soft fld-hint fld-gap">
                  {line}
                </p>
              ))}
              {/* El API ofrece el cierre solo si el LMS no reporta la etapa abierta. */}
              {t.canCloseTraining && (
                <CloseTrainingAction
                  entryId={entryId}
                  code={code}
                  onStale={() => void query.refetch()}
                />
              )}
              {t.canReschedule && (
                <button type="button" className="btn mut sm fld-gap" onClick={() => setOpen(true)}>
                  Reprogramar vigencia
                </button>
              )}
              <RescheduleModal
                open={open}
                current={t.dueDate}
                minDate={t.rescheduleMinDate}
                busy={reschedule.isPending}
                error={error}
                onCancel={close}
                onConfirm={confirm}
              />
            </div>
          );
        }}
      </QueryBody>
    </CardFrame>
  );
}
